import React, { useState, useEffect } from 'react';
import api from '../../utils/api';

const SCRAPE_TIMES_KEY = 'scrape_history';

const RefreshDataButton = ({ onRefreshComplete }) => {
  const [isScrapingInProgress, setIsScrapingInProgress] = useState(false);
  const [scrapeError, setScrapeError] = useState(null);
  const [scrapedCount, setScrapedCount] = useState(0);
  const [estimatedTimeRemaining, setEstimatedTimeRemaining] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [totalListings, setTotalListings] = useState(0);
  const [hasCompletedScrape, setHasCompletedScrape] = useState(false);

  // Load historical scrape times from localStorage
  const [historicalTimes, setHistoricalTimes] = useState(() => {
    const saved = localStorage.getItem(SCRAPE_TIMES_KEY);
    return saved ? JSON.parse(saved) : [];
  });

  // Check scraping status on component mount
  useEffect(() => {
    const checkInitialStatus = async () => {
      try {
        const { data } = await api.get('/scrape/status');
        
        if (data.isScrapingInProgress) {
          setIsScrapingInProgress(true);
          setScrapedCount(data.scrapedCount);
          setTotalListings(data.totalListings);
          setStartTime(data.startTime);
          
          // Calculate remaining time if we have historical data
          const history = historicalTimes;
          setHasCompletedScrape(history.length > 0);
          
          if (history.length > 0 && data.startTime) {
            const elapsedTime = (Date.now() - data.startTime) / 1000;
            const estimatedTotal = calculateEstimatedTime(data.totalListings);
            if (estimatedTotal !== null) {
              const remaining = Math.max(0, estimatedTotal - elapsedTime);
              setEstimatedTimeRemaining(Math.round(remaining));
            }
          }
        }
      } catch (error) {
        console.error('Error checking initial scrape status:', error);
      }
    };

    checkInitialStatus();
    const history = historicalTimes;
    setHasCompletedScrape(history.length > 0);
  }, []);

  // Save new scrape time to history
  const saveNewScrapeTime = (timeInSeconds) => {
    const history = [...historicalTimes];
    history.push({
      timestamp: new Date().toISOString(),
      duration: timeInSeconds,
      listingsCount: totalListings
    });
    // Keep only last 10 entries
    if (history.length > 10) history.shift();
    localStorage.setItem(SCRAPE_TIMES_KEY, JSON.stringify(history));
    setHistoricalTimes(history);
    setHasCompletedScrape(true);
  };

  // Calculate estimated time based on historical data
  const calculateEstimatedTime = (totalCount) => {
    const history = historicalTimes;
    if (history.length === 0) return null;

    // Calculate average time per listing
    const avgTimePerListing = history.reduce((acc, record) => {
      return acc + (record.duration / record.listingsCount);
    }, 0) / history.length;

    return Math.round(avgTimePerListing * totalCount);
  };

  // Update progress periodically
  useEffect(() => {
    let progressInterval;
    if (isScrapingInProgress && startTime) {
      progressInterval = setInterval(async () => {
        try {
          const { data } = await api.get('/scrape/status');
          setScrapedCount(data.scrapedCount);
          setTotalListings(data.totalListings);
          
          // Only calculate time remaining if we have historical data
          if (hasCompletedScrape) {
            const elapsedTime = (Date.now() - startTime) / 1000;
            const estimatedTotal = calculateEstimatedTime(data.totalListings);
            if (estimatedTotal !== null) {
              const remaining = Math.max(0, estimatedTotal - elapsedTime);
              setEstimatedTimeRemaining(Math.round(remaining));
            }
          }
        } catch (error) {
          console.error('Error fetching scrape status:', error);
        }
      }, 5000); // Reduced from 1000ms to 5000ms to reduce API calls
    }
    return () => clearInterval(progressInterval);
  }, [isScrapingInProgress, startTime, hasCompletedScrape]);

  const formatTime = (seconds) => {
    if (!seconds) return '';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const handleRefreshData = async () => {
    if (isScrapingInProgress) {
      // Stop the scraping
      try {
        setIsScrapingInProgress(true); // Keep showing loading state while stopping
        setScrapeError(null);
        
        const { data } = await api.post('/scrape/stop');
        
        // Wait for the scraping to actually stop
        let attempts = 0;
        const maxAttempts = 20; // Wait up to 20 seconds
        let stoppedSuccessfully = false;
        
        while (attempts < maxAttempts) {
          const { data: statusData } = await api.get('/scrape/status');
          
          if (!statusData.isScrapingInProgress) {
            stoppedSuccessfully = true;
            break;
          }
          
          await new Promise(resolve => setTimeout(resolve, 1000));
          attempts++;
        }
        
        if (!stoppedSuccessfully) {
          throw new Error('Timed out waiting for scraping to stop');
        }
        
                setIsScrapingInProgress(false);
        setScrapedCount(0);
        setEstimatedTimeRemaining(null);
        setStartTime(null);
        if (onRefreshComplete) {
          onRefreshComplete();
        }
        } catch (error) {
          console.error('Error stopping scrape:', error);
          setScrapeError(error.message || 'Failed to stop scraping process');
      }
      return;
    }

    try {
      setIsScrapingInProgress(true);
      setScrapeError(null);
      setStartTime(Date.now());
      
      const { data } = await api.post('/scrape');
      
      // Initial status check
      const { data: statusData } = await api.get('/scrape/status');
      setTotalListings(statusData.totalListings);
      if (hasCompletedScrape) {
        setEstimatedTimeRemaining(calculateEstimatedTime(statusData.totalListings));
      }
      
    } catch (error) {
      console.error('Error triggering scrape:', error);
      setScrapeError(error.message || 'Failed to start scraping process');
      setIsScrapingInProgress(false);
      setStartTime(null);
    }
  };

  // Handle scrape completion
  useEffect(() => {
    if (!isScrapingInProgress && startTime && scrapedCount > 0) {
      const duration = (Date.now() - startTime) / 1000;
      saveNewScrapeTime(duration);
      if (onRefreshComplete) {
        onRefreshComplete();
      }
    }
  }, [isScrapingInProgress, startTime, scrapedCount]);

  return (
    <div className="relative flex items-center space-x-3">
      <button
        onClick={handleRefreshData}
        disabled={false} // Never disable the button so we can stop scraping
        className={`
          relative
          inline-flex items-center px-4 py-2 rounded-md text-sm font-medium
          transition-all duration-200 ease-in-out
          shadow-sm hover:shadow-md
          ${isScrapingInProgress
            ? 'bg-gradient-to-r from-red-400 to-red-500 hover:from-red-500 hover:to-red-400 text-white'
            : 'bg-gradient-to-r from-[#96ffa8] to-[#72d684] hover:from-[#72d684] hover:to-[#96ffa8] text-black'
          }
          transform hover:-translate-y-0.5
          focus:outline-none focus:ring-2 focus:ring-[#96ffa8] focus:ring-opacity-50
        `}
      >
        <svg 
          className={`w-4 h-4 mr-2 ${isScrapingInProgress ? 'animate-spin' : ''}`} 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          {isScrapingInProgress ? (
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M6 18L18 6M6 6l12 12"
            />
          ) : (
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" 
            />
          )}
        </svg>
        <span className="relative">
          {isScrapingInProgress ? 'Stop Scraping' : 'Refresh Data'}
        </span>
      </button>

      {/* Progress Information */}
      {isScrapingInProgress && (
        <div className="text-sm text-gray-600 bg-white bg-opacity-95 px-3 py-2 rounded-md shadow-sm border border-[#96ffa8]">
          <div className="flex items-center space-x-2">
            <span className="font-medium">{scrapedCount}</span>
            <span className="text-gray-500">of</span>
            <span className="font-medium">{totalListings}</span>
            <span className="text-gray-500">listings</span>
            {hasCompletedScrape && estimatedTimeRemaining !== null && (
              <>
                <span className="text-gray-500">•</span>
                <span>{formatTime(estimatedTimeRemaining)} remaining</span>
              </>
            )}
          </div>
        </div>
      )}

      {/* Error Message */}
      {scrapeError && (
        <div className="absolute top-full left-0 mt-2 w-64 z-10">
          <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-md shadow-sm border border-red-100">
            Error: {scrapeError}
          </div>
        </div>
      )}
    </div>
  );
};

export default RefreshDataButton; 